import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import RootLayout from "./components/RootLayout";
import PremisesPage from "./pages/PremisesPage";
import CompaniesPage from "./pages/CompaniesPage";
import DemoStudioPage from "./pages/DemoStudioPage";
import ShowcasePage from "./pages/showcase/ShowcasePage";
import ShowcaseVideoPage from "./pages/showcase/ShowcaseVideoPage";
import RoomTypesPage from "./pages/RoomTypesPage";
import RadioStationsPage from "./pages/RadioStationsPage";
import AppCatalogPage from "./pages/AppCatalogPage";
import GlobalTemplatesPage from "./pages/GlobalTemplatesPage";
import GlobalMediaPage from "./pages/GlobalMediaPage";
import DistributionPage from "./pages/DistributionPage";
import LoginPage from "./pages/LoginPage";
import UsersPage from "./pages/UsersPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ContentAnalyticsPage from "./pages/ContentAnalyticsPage";
import SupplierRentalsPage from "./pages/supplier/SupplierRentalsPage";
import OtaPage from "./pages/OtaPage";
import DeviceGroupsPage from "./pages/DeviceGroupsPage";
import HealthAlertsPage from "./pages/HealthAlertsPage";
import MeteredUsagePage from "./pages/MeteredUsagePage";
import ClientErrorsPage from "./pages/ClientErrorsPage";
import GeneralSettingsPage from "./pages/GeneralSettingsPage";
import SignageOverviewPage from "./pages/SignageOverviewPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ScopeProvider } from "./context/ScopeContext";
import { PremiseWorkspaceProvider } from "./context/PremiseWorkspace";
import WorkspacePage from "./pages/WorkspacePage";
import PremiseRedirect from "./pages/PremiseRedirect";
import RequireAuth from "./auth/RequireAuth";
import { RequireAccess } from "./auth/access";
import { PAGE_ACCESS } from "./nav/pageAccess";
import OverviewRedirect from "./pages/OverviewRedirect";
import SupplierShell from "./components/SupplierShell";
import SupplierTitlesPage from "./pages/supplier/SupplierTitlesPage";
import SupplierTitlePage from "./pages/supplier/SupplierTitlePage";
import SupplierBrandPage from "./pages/supplier/SupplierBrandPage";
import SupplierUploadPage from "./pages/supplier/SupplierUploadPage";
import SupplierRevenuePage from "./pages/supplier/SupplierRevenuePage";
import SupplierAgreementsPage from "./pages/supplier/SupplierAgreementsPage";
import SupplierCustomersPage from "./pages/supplier/SupplierCustomersPage";
import SupplierCustomerPage from "./pages/supplier/SupplierCustomerPage";
import SupplierTeamPage from "./pages/supplier/SupplierTeamPage";
import SupplierPlansPage from "./pages/supplier/SupplierPlansPage";
import SupplierInformationPage from "./pages/supplier/SupplierInformationPage";
import SupplierReportsPage from "./pages/supplier/SupplierReportsPage";
import SupplierApplicationsPage from "./pages/supplier/SupplierApplicationsPage";
import { isContentOwnerSession, landingPathFor } from "./auth/audience";
import { canActForOwners } from "./auth/actingOwner";
import { actingOwner } from "./http/foyer";

/**
 * Landing page. Fleet health is the default home, but it needs `canViewFleet`
 * - a user without it (a premise editor, say) would land on a page the API
 * refuses, so they keep the old Premises home. A content owner lands in their
 * own shell, which is a different building entirely (`CONTENT-4`).
 */
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={landingPathFor(user)} replace />;
}

/**
 * The operator CMS, closed to content owners (`CONTENT-4`).
 *
 * Redirect rather than a bare refusal: an external party typing `/fleet` has
 * made a mistake, not an attack, and sending them home is both kinder and
 * quieter than a page that names a surface they cannot have. The refusal that
 * MATTERS is the server's - every one of these pages is gated there too.
 */
function StaffOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (isContentOwnerSession(user)) {
    return <Navigate to="/supplier/titles" replace />;
  }
  return <>{children}</>;
}

/**
 * The supplier shell, closed to everybody else - see {@link StaffOnly}.
 *
 * Two kinds of session get in: a supplier's own login, and a Nenda admin that
 * opened one owner's shell from the purchase-portal drawer (2026-10-14). An
 * admin with no owner open is sent to the content providers to pick one.
 */
function OwnerOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (isContentOwnerSession(user)) return <>{children}</>;
  if (canActForOwners(user)) {
    return actingOwner() ? (
      <>{children}</>
    ) : (
      <Navigate to="/distribution/providers" replace />
    );
  }
  return <Navigate to={landingPathFor(user)} replace />;
}

// Vite injects BASE_URL based on the config's `base` option. In dev the CMS
// runs at "/", in production at "/cms/". React Router needs to know which
// so internal navigation rewrites correctly.
const ROUTER_BASENAME =
  (import.meta.env.BASE_URL ?? "/").replace(/\/+$/, "") || undefined;

export default function App() {
  return (
    <BrowserRouter basename={ROUTER_BASENAME}>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          {/* The supplier shell. Same auth, same build - a different
              building. Registered before the operator routes so its own
              layout, not RootLayout, is what a content owner ever sees. */}
          <Route
            element={
              <RequireAuth>
                <OwnerOnly>
                  <SupplierShell />
                </OwnerOnly>
              </RequireAuth>
            }
          >
            <Route
              path="/supplier"
              element={<Navigate to="/supplier/titles" replace />}
            />
            <Route path="/supplier/titles" element={<SupplierTitlesPage />} />
            <Route
              path="/supplier/titles/:assetId"
              element={<SupplierTitlePage />}
            />
            <Route
              path="/supplier/customers"
              element={<SupplierCustomersPage />}
            />
            <Route
              path="/supplier/customers/:accountId"
              element={<SupplierCustomerPage />}
            />
            <Route path="/supplier/upload" element={<SupplierUploadPage />} />
            <Route
              path="/supplier/information"
              element={<SupplierInformationPage />}
            />
            <Route
              path="/supplier/audience"
              element={
                // The owner surface of the playback analytics: its titles +
                // its portal's catalogue on hotel/care TVs; no premise names,
                // no quality (server-enforced).
                <ContentAnalyticsPage surface="owner" />
              }
            />
            <Route path="/supplier/revenue" element={<SupplierRevenuePage />} />
            <Route path="/supplier/plans" element={<SupplierPlansPage />} />
            <Route path="/supplier/reports" element={<SupplierReportsPage />} />
            <Route
              path="/supplier/applications"
              element={<SupplierApplicationsPage />}
            />
            <Route path="/supplier/rentals" element={<SupplierRentalsPage />} />
            <Route
              path="/supplier/agreements"
              element={<SupplierAgreementsPage />}
            />
            <Route path="/supplier/brand" element={<SupplierBrandPage />} />
            <Route path="/supplier/team" element={<SupplierTeamPage />} />
          </Route>

          <Route
            element={
              <RequireAuth>
                <StaffOnly>
                  {/* Above RootLayout: the sidebar itself reads the scope. */}
                  <ScopeProvider>
                    <PremiseWorkspaceProvider>
                      <RootLayout />
                    </PremiseWorkspaceProvider>
                  </ScopeProvider>
                </StaffOnly>
              </RequireAuth>
            }
          >
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/premises" element={<PremisesPage />} />
            {/* Opening a premise scopes the header to it and hands off to the
                product areas - there is no separate premise editor any more. */}
            <Route path="/premises/:premiseId" element={<PremiseRedirect />} />

            {/* Product areas. The section is a route param; the premise comes
                from the header scope. */}
            <Route
              path="/places/:section"
              element={<WorkspacePage area="Location" />}
            />
            <Route
              path="/frontlayer/:section"
              element={<WorkspacePage area="Frontlayer" />}
            />
            {/* Deeper levels: Start view → Information → Own / Standard sections. */}
            <Route
              path="/frontlayer/:section/:sub"
              element={<WorkspacePage area="Frontlayer" />}
            />
            <Route
              path="/frontlayer/:section/:sub/:leaf"
              element={<WorkspacePage area="Frontlayer" />}
            />
            <Route
              path="/guest-web"
              element={<WorkspacePage area="Guest web" section="guest-web" />}
            />
            <Route
              path="/promotions/:section"
              element={<WorkspacePage area="Promotions" />}
            />
            <Route
              path="/care/:section"
              element={<WorkspacePage area="Care" />}
            />
            <Route
              path="/video-calling/:section"
              element={<WorkspacePage area="Video calling" />}
            />
            <Route
              path="/signage/:section"
              element={<WorkspacePage area="Signage" />}
            />
            <Route
              path="/streaming-tv/:section"
              element={<WorkspacePage area="Streaming TV" />}
            />
            {/* Guarded here as well as hidden in the menu, because the URL
                bar is a second way in. */}
            <Route element={<RequireAccess page={PAGE_ACCESS.companies} />}>
              <Route path="/companies" element={<CompaniesPage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.demoStudio} />}>
              <Route path="/demo-studio" element={<DemoStudioPage />} />
              <Route
                path="/demo-studio/:sessionId"
                element={<DemoStudioPage />}
              />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.roomTypes} />}>
              <Route path="/room-types" element={<RoomTypesPage />} />
            </Route>
            {/* The standalone Fleet overview was merged into the analytics
                page; keep the old path working (deep-links carry ?status=). */}
            <Route
              path="/fleet"
              element={<Navigate to="/analytics" replace />}
            />
            <Route element={<RequireAccess page={PAGE_ACCESS.fleetHealth} />}>
              <Route path="/analytics" element={<AnalyticsPage />} />
            </Route>
            {/* A customer's landing: resolves to their Frontlayer or Signage
                overview by the header's product selector. */}
            <Route path="/overview" element={<OverviewRedirect />} />
            {/* Chain-level CONTENT analytics (what guests use), distinct from
                /analytics which is the device fleet's health. */}
            <Route
              element={<RequireAccess page={PAGE_ACCESS.contentAnalytics} />}
            >
              <Route
                path="/content-analytics"
                element={<ContentAnalyticsPage />}
              />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.ota} />}>
              <Route path="/ota" element={<OtaPage />} />
            </Route>
            {/* The signage estate view. A fleet-wide page like its siblings
                above, scoped from the header bar rather than from the URL. */}
            <Route
              element={<RequireAccess page={PAGE_ACCESS.signageOverview} />}
            >
              <Route
                path="/signage-overview"
                element={<SignageOverviewPage />}
              />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.deviceGroups} />}>
              <Route path="/device-groups" element={<DeviceGroupsPage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.healthAlerts} />}>
              <Route path="/health-alerts" element={<HealthAlertsPage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.meteredUsage} />}>
              <Route path="/metered-usage" element={<MeteredUsagePage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.salesVideos} />}>
              <Route path="/video" element={<ShowcasePage />} />
              <Route path="/video/:id" element={<ShowcaseVideoPage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.clientErrors} />}>
              <Route path="/client-errors" element={<ClientErrorsPage />} />
            </Route>
            <Route
              element={<RequireAccess page={PAGE_ACCESS.generalSettings} />}
            >
              <Route
                path="/settings/general"
                element={<GeneralSettingsPage />}
              />
            </Route>
            <Route
              element={<RequireAccess page={PAGE_ACCESS.templateDefaults} />}
            >
              <Route path="/templates" element={<GlobalTemplatesPage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.radioStations} />}>
              <Route path="/radio-stations" element={<RadioStationsPage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.appCatalogue} />}>
              <Route path="/apps" element={<AppCatalogPage />} />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.globalMedia} />}>
              <Route path="/media" element={<GlobalMediaPage />} />
            </Route>
            {/* Platform-level distribution. One route, four sections - each
                becomes a real editor by replacing its branch. */}
            <Route
              path="/distribution"
              element={<Navigate to="/distribution/channels" replace />}
            />
            <Route element={<RequireAccess page={PAGE_ACCESS.distribution} />}>
              <Route
                path="/distribution/:section"
                element={<DistributionPage />}
              />
            </Route>
            <Route element={<RequireAccess page={PAGE_ACCESS.users} />}>
              <Route path="/users" element={<UsersPage />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
